/**
 * ENG-5684 — the staff-wide engagements list.
 *
 * Every engagement on the platform, for OF internal staff. Backed by ENG-5438's
 * staff feed, which does its own gating: a non-staff caller is denied with a
 * permission error, not served an empty page. The tab that mounts this is
 * hidden for non-staff, so a denial here means a stale session rather than a
 * snoop.
 *
 * Pagination copies `/expert_requests` (`ExpertRequestList.tsx`): cursor from
 * the last edge, `LongList` with `hasNextPage` + `onMore`, pages appended.
 * Ordering is the server's — newest first — and there is no sort control, same
 * as that page. The prototype's sortable column headers are deliberately not
 * here: client-side sorting reorders only the page in hand, which goes wrong
 * the moment a second page loads.
 *
 * The filter pills are the prototype's, and the api's `EngagementFeedBucket`
 * enum was minted to match them, so the two vocabularies cannot drift.
 */
import { useQuery } from '@apollo/client';
import { useRef, useState } from 'react';

import { EngagementFeedBucket } from '@/__generated__/graphql';
import LongList from '@/components/LongList/LongList';
import Button from '@/componentsv2/Button';

import StaffFeedRow from './StaffFeedRow';
import { STAFF_ENGAGEMENTS } from './operations';
import {
  STAFF_BUCKETS,
  STAFF_FILTER_LABELS,
  type StaffFeedBucket,
  fromStaffFeed,
} from './staffFeedViewModel';

const PAGE_SIZE = 10;

const HEADER_CELL =
  'px-12 py-10 text-left util-caption font-medium uppercase tracking-wider text-on-surface-muted';

// "NTE (consumed)", not the prototype's "NTE (remaining)". The prototype's
// hint named its SORT key, which this surface does not offer; the cell has
// always rendered consumed-of-total, so "remaining" labelled the one number in
// the column that is not shown.
const COLUMNS = [
  'Customer / Project',
  'Expert',
  'Status',
  'NTE (consumed)',
  'Red flags',
  // "Last EXPERT activity", because that is what the column holds and it is a
  // different clock from the one the list is ordered by (the server sorts on
  // the engagement's activation floor). Naming it plainly, and stating the
  // ordering next to the count, is what stops the column reading as a broken
  // sort.
  'Last expert activity',
];

export default function StaffEngagementsList(): JSX.Element {
  const [bucket, setBucket] = useState<StaffFeedBucket>(EngagementFeedBucket.All);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);

  const { data, loading, error, refetch, fetchMore } = useQuery(STAFF_ENGAGEMENTS, {
    variables: { bucket, first: PAGE_SIZE, after: null },
    // network-only, not cache-and-network. The merged multi-page result is
    // written back under the FIRST page's cache key, so a cache-first paint on
    // remount (switching to Mine and back unmounts this list) shows every page
    // loaded last time and then silently collapses to 10 rows when the network
    // reply lands — and a Load More inside that window pages from a cursor the
    // list no longer has, dropping everything between. Always refetching page
    // one costs a query per tab switch and cannot be silently wrong.
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
  });

  const edges = data?.engagements?.edges ?? [];
  const hasNextPage = Boolean(data?.engagements?.pageInfo?.hasNextPage);
  const total = data?.engagements?.pageInfo?.total ?? null;

  // Pinned per bucket rather than per render: a fresh `new Date()` on every
  // render would give each relative timestamp a new value and rerender the
  // whole table on any state change.
  const rows = fromStaffFeed(edges);

  // The bucket as of the click, checked again when the page lands. Apollo's
  // updateQuery writes through `cache.updateQuery({variables: this.variables})`
  // — this.variables as of RESOLUTION, not of the call — so hitting Load More
  // on All and then switching to Pending before it returns appends All's second
  // page onto Pending's cache entry. Dropping the late page is the honest
  // outcome: the user is looking at a different filter, and hasNextPage for
  // that filter is still true, so the button remains available.
  const bucketRef = useRef(bucket);
  bucketRef.current = bucket;

  function handleMore() {
    if (!hasNextPage || loading || edges.length === 0) return;
    const requestedBucket = bucket;
    setLoadMoreFailed(false);
    void fetchMore({
      variables: { after: edges[edges.length - 1]?.cursor },
      updateQuery: (previous, { fetchMoreResult }) => {
        if (!fetchMoreResult?.engagements) return previous;
        if (bucketRef.current !== requestedBucket) return previous;
        return {
          engagements: {
            ...fetchMoreResult.engagements,
            edges: [
              ...(previous.engagements?.edges ?? []),
              ...(fetchMoreResult.engagements.edges ?? []),
            ],
          },
        };
      },
      // A rejected fetchMore is otherwise an unhandled rejection with no user
      // feedback: the page silently does not grow and the operator is left
      // deciding whether the list simply ended.
    }).catch(() => setLoadMoreFailed(true));
  }

  if (error) {
    // Deliberately does NOT diagnose. This branch catches a dropped connection,
    // a 502 and a permission denial alike, and the first cut told all three to
    // sign out and back in — advice that is wrong for two of them and hides a
    // retryable failure behind an auth story. Offer the retry; say what is
    // known.
    return (
      <div className="rounded-lg border border-surface-border bg-surface-card p-24">
        <h2 className="text-on-surface hd-5">We couldn&rsquo;t load the engagement list.</h2>
        <p className="mt-8 text-on-surface-muted body-2">
          The connection may have dropped. This list is also OnFrontiers staff only, so if retrying
          does not help, check that you are still signed in as a staff account.
        </p>
        <div className="mt-16">
          <Button onClick={() => void refetch()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-20">
      <p className="text-on-surface-muted body-2">
        Read-only across every customer. Opening an engagement here does not announce you in its
        messages.
      </p>

      <div className="flex flex-wrap items-center gap-8" role="tablist" aria-label="Status filter">
        {STAFF_BUCKETS.map((b) => (
          <button
            key={b}
            type="button"
            role="tab"
            aria-selected={bucket === b}
            onClick={() => {
              setLoadMoreFailed(false);
              setBucket(b);
            }}
            className={`min-h-[44px] rounded-full border px-12 py-6 transition-colors subtitle-2 ${
              bucket === b
                ? 'border-brand-primary-faint bg-brand-light text-on-surface'
                : 'border-surface-border bg-surface-elevated text-on-surface-muted hover:bg-surface-secondary'
            }`}
          >
            {STAFF_FILTER_LABELS[b]}
          </button>
        ))}
      </div>

      <LongList
        loading={loading && rows.length === 0}
        title={
          total === null
            ? 'Engagements — newest first'
            : `${total.toLocaleString('en-US')} engagements — newest first`
        }
        hasNextPage={hasNextPage && rows.length > 0}
        onMore={handleMore}
      >
        {rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-surface-border bg-surface-elevated px-24 py-48 text-center">
            <p className="text-on-surface-muted body-2">No engagements match the current filter.</p>
          </div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-surface-border bg-surface-card">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-surface-border bg-surface-secondary">
                  {COLUMNS.map((label) => (
                    <th key={label} className={HEADER_CELL} scope="col">
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <StaffFeedRow key={row.engagementId} row={row} />
                ))}
              </tbody>
            </table>
          </div>
        )}
        {loadMoreFailed ? (
          <p className="mt-12 text-on-surface-muted body-2" role="status">
            We couldn&rsquo;t load more engagements.{' '}
            <button
              type="button"
              className="underline"
              onClick={() => {
                setLoadMoreFailed(false);
                handleMore();
              }}
            >
              Try again
            </button>
          </p>
        ) : null}
      </LongList>
    </div>
  );
}
